import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { pipe } from 'Lib/Hooks/Selectors';
import React from 'react';
import { useSelector } from 'react-redux';
import { Field, RadioGroup } from '../../../../Core';
import { withInjectedProps } from '../../../../Core/InjectedPropsContext';
import { ListFieldSection } from '../../../../Layout';
import { MaterialDriedBy, PreparationMethod, SampleHistory, WashingMethod } from '../types';
import AsAssociatedPercentagePassing from './Subsections/AssociatedPercentagePassingSection';
import AsOvenSection from './Subsections/OvenSection';
import { AsPreparationDrySection } from './Subsections/PreparationDrySection';
import AsPreparationAlternativeSection from './Subsections/PreparationNaturalStateSection';
import AsPreparationSieveSection from './Subsections/PreparationSieveSection';
import { AsPreparationWetSection } from './Subsections/PreparationWetSection';

/* 
  Defines a shared AS Sample Preparation Section
  Used in the following tests:
  Linear Shrinkage [AS 1289.3.4.1 - 2008]
  Atterberg Limits (Four Point Casagrande) [AS 1289.3.1.1, 3.2.1, 3.3.1 - 2009]
  Atterberg Limits (One Point Casagrande) [AS 1289.3.1.2, 3.2.1, 3.3.1 - 2009]
*/
let AsSamplePreparationSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const PreparationMethodValue = useSelector(pipe(createWorksheetSelector!(`PreparationMethod`)));
  const ovenDried = useSelector(pipe(createWorksheetSelector!(`MaterialDriedBy`), (d) => d === MaterialDriedBy.Oven));
  const StandardWashingMethod = useSelector(
    pipe(createWorksheetSelector!(`WashingMethod`), (d) => d === WashingMethod.FullWash)
  );
  const GetPreparationSubSection = (PreparationMethodVal: any) => {
    switch (PreparationMethodVal) {
      case PreparationMethod.Dry:
        return <AsPreparationDrySection />;
      case PreparationMethod.Wet:
        return <AsPreparationWetSection />;
      case PreparationMethod.Natural:
      default:
        return <AsPreparationAlternativeSection />;
    }
  };

  return (
    <>
      <ListFieldSection>
        <Field
          input={<RadioGroup />}
          name="SampleHistory"
          options={[
            SampleHistory.Natural,
            SampleHistory.AirDried,
            SampleHistory.OvenDried,
            SampleHistory.Unknown,
          ].map((e) => ({ label: e, value: e }))}
        ></Field>
        <Field
          input={<RadioGroup />}
          name="PreparationMethod"
          options={[PreparationMethod.Wet, PreparationMethod.Dry, PreparationMethod.Natural].map((e) => ({
            label: e,
            value: e,
          }))}
        ></Field>
      </ListFieldSection>
      {GetPreparationSubSection(PreparationMethodValue)}
      {(ovenDried || (StandardWashingMethod && PreparationMethodValue === PreparationMethod.Wet)) && <AsOvenSection />}

      <AsPreparationSieveSection />
      <AsAssociatedPercentagePassing />
    </>
  );
};

AsSamplePreparationSection = withInjectedProps(AsSamplePreparationSection);

export { AsSamplePreparationSection };
export default AsSamplePreparationSection;
